import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Avatar } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as LogoutIcon } from '../../../../../../assets/icons/v3/logout.svg';
import { ReactComponent as SettingsIcon } from '../../../../../../assets/icons/v3/settings.svg';
import ButterflyGreen from '../../../../../../assets/images/butterfly-green.png';
import { ACCOUNT_ROUTE, INTEGRATIONS_ROUTE } from '../../../../../constants/auth';
import { CAN_SEE_INTEGRATIONS_FG } from '../../../../../constants/statsig';
import { SessionContext } from '../../../../../context/auth/authContext';
import { useFeatureGate } from '../../../../../hooks/useFeatureGate/useFeatureGate';
import useOutsideComponentClick from '../../../../../hooks/useOutsideComponentClick/outsideComponentClick';
import { ISidebarUserMenuItem } from '../../../../../interfaces/appNavbar';
import { TRANSLATIONS_GLOBAL } from '../../../../../translations/commons';
import { getInitialsLetters } from '../../../../../utils/utils';
import SidebarUserMenu from '../SidebarUserMenu/SidebarUserMenu';

import './SidebarUserProfile.scss';

interface SidebarUserProfileProps {
  isExpanded: boolean;
}

const SidebarUserProfile = ({ isExpanded }: SidebarUserProfileProps) => {
  const [openMenu, setOpenMenu] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  const { user, sessionLogout } = useContext(SessionContext);
  const { isEnabled: canSeeIntegrations } = useFeatureGate(CAN_SEE_INTEGRATIONS_FG);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const username = user.name || user.email.split('@')[0];

  const closeMenu = useCallback(() => setOpenMenu(false), []);

  useOutsideComponentClick(containerRef, closeMenu, openMenu);

  useEffect(() => {
    if (!isExpanded) {
      setOpenMenu(false);
    }
  }, [isExpanded]);

  const menuItems = useMemo<ISidebarUserMenuItem[]>(() => {
    const goTo = (route: string) => () => {
      setOpenMenu(false);
      void navigate(route);
    };

    const items: ISidebarUserMenuItem[] = [
      {
        key: 'settings',
        label: t(TRANSLATIONS_GLOBAL.sidebar.userMenu.settings),
        icon: SettingsIcon,
        onClick: goTo(ACCOUNT_ROUTE)
      }
    ];

    if (canSeeIntegrations) {
      items.push({
        key: 'integrations',
        label: t(TRANSLATIONS_GLOBAL.sidebar.userMenu.integrations),
        onClick: goTo(INTEGRATIONS_ROUTE)
      });
    }

    items.push({
      key: 'logout',
      label: t(TRANSLATIONS_GLOBAL.sidebar.userMenu.logout),
      icon: LogoutIcon,
      onClick: () => {
        setOpenMenu(false);
        void sessionLogout();
      }
    });

    return items;
  }, [canSeeIntegrations, navigate, sessionLogout, t]);

  return (
    <div className={`sidebar-user-profile ${isExpanded ? 'expanded' : ''}`} ref={containerRef}>
      {openMenu && <SidebarUserMenu items={menuItems} />}
      <button
        type="button"
        className="sidebar-user-profile-button"
        aria-label={username}
        aria-haspopup="menu"
        aria-expanded={openMenu}
        onClick={() => setOpenMenu((prev) => !prev)}
      >
        <span className="sidebar-user-profile-avatar-wrapper">
          <Avatar className="sidebar-user-profile-avatar" alt={username} src={user.image || undefined}>
            {getInitialsLetters(username, ' ')}
          </Avatar>
          {!isExpanded && (
            <img className="sidebar-user-profile-butterfly on-avatar" src={ButterflyGreen} alt="" aria-hidden="true" />
          )}
        </span>
        {isExpanded && (
          <span className="sidebar-user-profile-name-wrapper">
            <span className="sidebar-user-profile-name" title={username}>
              {username}
            </span>
            <img className="sidebar-user-profile-butterfly on-name" src={ButterflyGreen} alt="" aria-hidden="true" />
          </span>
        )}
      </button>
    </div>
  );
};

export default SidebarUserProfile;
